
import { useEffect, useRef, useState } from 'react';
import { api, buildQuery } from '../../shared/api';

const runRequest = async ({
    query,
    queryVariables,
    abortController,
}) => {

    const result = await api.executeGetRequest(
        buildQuery(query, queryVariables),
        abortController
    );

    if (result.errors?.length) {
        throw new Error(
            result.errors?.[0]?.error_message || 'Unknown network error occurred'
        );
    }

    if (!result?.data) {
        throw new Error(
            'Has no data attribute in network response'
        );
    }

    return { data: result.data };
};

export const useFetchData = <TData>({
    query,
    selectData,
}) => {
    const [data, setData] = useState<TData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean | null>(null);
    const [error, setError] = useState<Error | null>(null);

    const abortController = useRef<AbortController | null>(null);

    useEffect(() => {
        return () => {
            if (abortController.current) {
                abortController.current.abort();
            }
        };
    }, []);

    const fetchData = async (queryVariables = {}) => {
        if (abortController.current) {
            abortController.current.abort();
        }
        abortController.current = new AbortController();

        setIsLoading(true);
        setError(null);

        try {
            const result = await runRequest({
                query,
                queryVariables,
                abortController: abortController.current,
            });

            setData(selectData(result.data));

        } catch (err: any) {
            if (err.name === 'AbortError') {
                console.log('Request aborted');
                return;
            }

            console.error(err.message);
            setError(err);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (!isLoading && abortController.current) {
            abortController.current = null;
        }
    }, [isLoading]);

    const clearError = () => setError(null)
    const clearData = () => {

        if (abortController.current) {
            abortController.current.abort();
            abortController.current = null;
        }

        setData(null);
        setIsLoading(null);
        setError(null);
    }

    return [fetchData, { isLoading, data, error, clearError, clearData }] as const;
};
